import { useEffect, useState } from "react";
import breakpoints from "../lib/breakpoints.json";

// Where the toast rests and how it moves.
//
// Issue #1384 (D367.14, Andreas, 2026-10-20): from the md breakpoint up the toast rests at the BOTTOM of the
// viewport, the phone convention, so it is never drawn over the top bar, the ribbon, a demo banner's Switch to
// Player or a section strip's buttons. That replaces #1271's and #1304's measured clearance (--nw-toast-clear,
// computed from the banner, the bar and the ribbon): a card that rests at the bottom has nothing above it to
// clear. Below it the toast is where it was (top, 80px).
//
// #1428 (D383) moved the split from sonner's 600px/601px to the md breakpoint, 768px
// (`lib/breakpoints.json`, the one place the value is written): 601px to 767px are phone widths now, and 768px
// is the first width the toast rests at the bottom. index.css and the e2e harness mirror the same number.

export const TOAST_WIDE_QUERY = `(min-width: ${breakpoints.md}px)`;

/** True while the viewport is at the md breakpoint (768px) or wider; follows resizes. */
export function useToastRestsAtBottom() {
  const read = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(TOAST_WIDE_QUERY).matches : false);
  const [wide, setWide] = useState(read);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(TOAST_WIDE_QUERY);
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide;
}

/**
 * #1394 (D366): a toast card that is moving (sliding in or out) slides across the page, so
 * its close takes no taps until it stops. `data-nw-moving` is on the card from its first running
 * transition to its last one's end or cancel (index.css reads it); a card with nothing animating
 * (reduced motion) never gets it. State, not a duration. Exported for the unit test.
 */
export function trackToastMotion(doc = document) {
  const running = new WeakMap();
  const card = (e) => (e.target && e.target.closest ? e.target.closest("[data-sonner-toast]") : null);
  const start = (e) => {
    const c = card(e);
    if (!c) return;
    running.set(c, (running.get(c) || 0) + 1);
    c.setAttribute("data-nw-moving", "");
  };
  const stop = (e) => {
    const c = card(e);
    if (!c) return;
    const n = Math.max(0, (running.get(c) || 0) - 1);
    running.set(c, n);
    if (n === 0) c.removeAttribute("data-nw-moving");
  };
  doc.addEventListener("transitionrun", start, true);
  doc.addEventListener("transitionend", stop, true);
  doc.addEventListener("transitioncancel", stop, true);
  return () => {
    doc.removeEventListener("transitionrun", start, true);
    doc.removeEventListener("transitionend", stop, true);
    doc.removeEventListener("transitioncancel", stop, true);
  };
}

export default function ToastMotion() {
  useEffect(() => trackToastMotion(document), []);
  return null;
}
